import { type ColumnDef } from '@tanstack/react-table';
import { useCallback, useEffect, useState } from 'react';
import {
  useDataProvider,
  useDataProviderPaginated,
} from '../../../hooks/data-provider/data-provider.hook';
import { SortBy } from '../../../interfaces/sort-by.interface';
import {
  AppTabularColumn,
  AppTabularColumnGroup,
} from '../dtos/app-tabular.dto';
import { ReportsComponentProps } from '../interfaces/reports-component-props';
import { DataTableComponent } from './data-table.component';
import './generic-table.component.scss';

export interface CommonTableComponentProps {
  showTotal?: boolean;
  height?: number | string | undefined;
  totalReportName: string;
  defaultSortBy?: SortBy[];
  parseMeasuresColumns(
    state: AppTabularColumn[],
    data: any,
    total: any,
    columnGroups?: AppTabularColumnGroup[],
  ): ColumnDef<any, any>[];
  parseColumns(
    state: AppTabularColumn[],
    data: any,
    total: any,
    columnGroups?: AppTabularColumnGroup[],
  ): ColumnDef<any, any>[];
  startColumns?: any[];
  endColumns?: any[];
  onFlatData?(data: any[]): void;
  className?: string;
  enableRowSelection?: boolean;
  enableMultiRowSelection?: boolean;
  onRowSelectionChange?(row: any): void;
  idField?: string;
  setInvalidateQuery?: (value: { invalidateQuery: () => Promise<any> }) => void;
  enableStickyFirstColumn?: boolean;
  suppressRowClickSelection?: boolean;
}

interface GenericTableComponentProps
  extends ReportsComponentProps,
    CommonTableComponentProps {}

export const GenericTableComponent = ({
  onRowSelectionChange,
  enableRowSelection = false,
  enableMultiRowSelection,
  filterSelection,
  reportName,
  reportId,
  totalReportName,
  enabled,
  height,
  defaultSortBy,
  parseMeasuresColumns,
  parseColumns,
  startColumns,
  endColumns,
  showTotal = false,
  onFlatData,
  className,
  idField,
  setInvalidateQuery,
  enableStickyFirstColumn = false,
  suppressRowClickSelection,
}: GenericTableComponentProps) => {
  const [sortBy, setSortBy] = useState<SortBy[] | undefined>(defaultSortBy);
  const {
    data,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    invalidateQuery: invalidateQueryPaginated,
  } = useDataProviderPaginated<any>(
    reportName,
    filterSelection,
    sortBy,
    enabled,
    reportId ?? '',
  );
  const { data: totalData, invalidateQuery } = useDataProvider<any>(
    totalReportName,
    filterSelection,
    undefined,
    enabled,
    true,
    reportId ?? '',
  );
  const invalidate = useCallback(async () => {
    await invalidateQuery();
    await invalidateQueryPaginated();
  }, [invalidateQuery, invalidateQueryPaginated]);

  useEffect(() => {
    if (setInvalidateQuery) {
      setInvalidateQuery({ invalidateQuery: invalidate });
    }
  }, [invalidate, setInvalidateQuery]);

  return (
    <DataTableComponent
      data={data}
      totalData={totalData}
      isFetching={isFetching}
      isFetchingNextPage={isFetchingNextPage}
      hasNextPage={hasNextPage}
      fetchNextPage={fetchNextPage}
      parseColumns={parseColumns}
      parseMeasuresColumns={parseMeasuresColumns}
      startColumns={startColumns}
      endColumns={endColumns}
      onFlatData={onFlatData}
      sortBy={sortBy}
      setSortBy={setSortBy}
      className={className}
      showTotal={showTotal}
      height={height}
      idField={idField}
      enableRowSelection={enableRowSelection}
      enableMultiRowSelection={enableMultiRowSelection}
      onRowSelectionChange={onRowSelectionChange}
      enableStickyFirstColumn={enableStickyFirstColumn}
      suppressRowClickSelection={suppressRowClickSelection}
    />
  );
};
